import './AttachFilesFromExistingDocsExecutor.scss';
import React, { useEffect, useMemo, useState } from 'react';
import { MdOutlineInsertDriveFile } from 'react-icons/md';
import { IAttachListItemModel } from '@models/attaches/IAttachListItemModel';
import { useActions } from '@hooks/useActions';
import { AttachActionsService } from '@services/actions/AttachActionsService';
import Modal from '@srm/ui/modal';
import clsx from 'clsx';
import Button from '@srm/ui/button';
import ModalLoadingOverlay from '@srm/ui/modal-loading-overlay';
import Hint from '@srm/ui/hint';
import { stringifyError } from '@srm/core/errors';
import SelectableList from '@srm/ui/selectable-list';
import { IAttachFilesFromExistingDocsExecutorProps } from './IAttachFilesFromExistingDocsExecutorProps';

export const AttachFilesFromExistingDocsExecutorModal: React.FC<IAttachFilesFromExistingDocsExecutorProps> = (
    props: IAttachFilesFromExistingDocsExecutorProps,
) => {
    // Только загрузка списка: выбор файла применяется по «ОК» сразу, поэтому закрыть окно можно всегда.
    const [isBusy, setIsBusy] = useState<boolean>();
    const [errorText, setErrorText] = useState<string>();
    const [filesToSelect, setFilesToSelect] = useState<IAttachListItemModel[]>();
    const [checkedAttach, setCheckedAttach] = useState<string[]>([]);

    const { setPartsVersionToAttach } = useActions();

    useEffect(() => {
        let ignore = false;
        const service = new AttachActionsService(props.docId, '-1');
        if (props.payloadData.docIds) {
            setIsBusy(true);
            const ids = props.payloadData.docIds.split(',');
            service
                .getDocsAttaches(ids)
                .then((res) => {
                    if (!ignore) setFilesToSelect(res.data);
                })
                .catch((error: unknown) => {
                    if (!ignore) setErrorText(stringifyError(error));
                })
                .finally(() => {
                    if (!ignore) setIsBusy(false);
                });
        }
        return () => {
            ignore = true;
        };
    }, []);

    // Файл выбирается один: раньше это делалось ручным снятием остальных строк
    // после каждого клика, теперь режим одиночного выбора держит сам список.
    const items = useMemo(
        () =>
            (filesToSelect ?? []).map((file) => ({
                key: file.docIdAndPartNum,
                title: file.attachName,
                subtitle: file.docName,
                meta: file.isManualCreated ? 'создан вручную' : undefined,
                icon: <MdOutlineInsertDriveFile size={18} />,
            })),
        [filesToSelect],
    );

    return (
        <Modal
            className={clsx('modal-dlg-container')}
            size={props.modalSize}
            hasUnsavedInput={() => checkedAttach.length > 0}
            onClose={() => {
                if (props.completeHandler) props.completeHandler(false);
            }}
            header={props.action.displayName}
            footer={
                <div className="modal-dlg-buttons">
                    <Button
                        size="sm"
                        aria-label={props.okButtonText}
                        onClick={() => {
                            if (props.completeHandler) {
                                setPartsVersionToAttach(checkedAttach);
                                props.completeHandler(true);
                            }
                        }}
                        disabled={isBusy}
                    >
                        {props.okButtonText}
                    </Button>
                    <Button
                        variant="light"
                        size="sm"
                        aria-label={props.cancelButtonText}
                        onClick={() => {
                            if (props.completeHandler) props.completeHandler(false);
                        }}
                    >
                        {props.cancelButtonText}
                    </Button>
                </div>
            }
        >
            <ModalLoadingOverlay visible={!!isBusy}>
                <div>
                    {errorText && <Hint role="alert" title={`Ошибка: ${errorText}`} tone="danger" maxWidth="100%" />}
                    <SelectableList
                        mode="single"
                        items={items}
                        selected={checkedAttach}
                        onChange={setCheckedAttach}
                        emptyText="Файлов в выбранных документах нет"
                        ariaLabel="Файлы документов"
                    />
                </div>
            </ModalLoadingOverlay>
        </Modal>
    );
};
